import type { FC } from 'react';
import {
    Box,
    Button,
    Container,
    Typography,
    Card,
    Stack
} from '@mui/material';
import { motion } from 'framer-motion';

import PublicIcon from '@mui/icons-material/Public';
import PaymentIcon from '@mui/icons-material/Payment';
import TokenIcon from '@mui/icons-material/Token';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

interface StatItemProps {
    label: string;
    value: string;
    icon: React.ReactNode;
    index: number;
}

const StatItem: FC<StatItemProps> = ({ label, value, icon, index }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: index * 0.1 }}
        whileHover={{ scale: 1.05 }}
    >
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                px: { xs: 1.5, md: 2 },
                width: { xs: '100%', sm: 'auto' },
        

            }}
        >
            <motion.div
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.6 }}
            >
                <Box
                    sx={{
                        color: '#FF6B00',
                        mb: 0.5,
                        opacity: 0.9,
                        transform: 'scale(1.2)'
                    }}
                >
                    {icon}
                </Box>
            </motion.div>
            <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: index * 0.2, type: "spring", stiffness: 100 }}
            >
                <Typography
                    variant="h3"
                    sx={{
                        color: 'common.white',
                        fontWeight: 700,
                        mb: 0.5,
                        fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                        background: 'linear-gradient(90deg, #FF6B00 0%, #FF9D00 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                    }}
                >
                    {value}
                </Typography>
            </motion.div>
            <Typography
                variant="body2"
                sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    textAlign: 'center',
                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                    transition: 'color 0.3s ease',
                    '&:hover': {
                        color: '#FF6B00'
                    }
                }}
            >
                {label}
            </Typography>
        </Box>
    </motion.div>
);

export const HomeStats: FC = () => {
    

    return (
        <Box
            component="section"
            sx={{
                backgroundColor: '#0F1521',
                color: 'common.white',
                position: 'relative',
                overflow: 'hidden',
                py: { xs: 6, md: 10 },
                minHeight: { xs: 'auto', md: '100vh' },
                display: 'flex',
                alignItems: 'center'
            }}
        >
            {/* Animated gradient background */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    opacity: 0.1,
                    background: 'radial-gradient(circle at 50% 50%, #FF6B00 0%, transparent 50%)',
                    animation: 'pulse 8s ease-in-out infinite',
                    '@keyframes pulse': {
                        '0%': { transform: 'scale(1)' },
                        '50%': { transform: 'scale(1.5)' },
                        '100%': { transform: 'scale(1)' }
                    }
                }}
            />

            {/* Orange accent elements with animations */}
            <motion.div
                initial={{ height: 0 }}
                animate={{ height: '100px' }}
                transition={{ duration: 1 }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '15%',
                        left: '10%',
                        width: '2px',
                        backgroundColor: '#FF6B00',
                        opacity: 0.3,
                        zIndex: 0
                    }}
                />
            </motion.div>

            <motion.div
                initial={{ width: 0 }}
                animate={{ width: '100px' }}
                transition={{ duration: 1, delay: 0.5 }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: '20%',
                        left: '20%',
                        height: '2px',
                        backgroundColor: '#FF6B00',
                        opacity: 0.3,
                        zIndex: 0
                    }}
                />
            </motion.div>

            {/* Globe image with parallax effect */}
            <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 0.8, scale: 1 }}
                transition={{ duration: 1.5 }}
                style={{
                    position: 'absolute',
                    bottom: '-15%',
                    right: '0%',
                    width: '70%',
                    maxWidth: '1000px',
                    zIndex: 0,
                }}
            >
                <Box
                    component="img"
                    src="/globe.avif"
                    alt="Globe"
                    sx={{
                        width: '100%',
                        height: 'auto',
                        filter: 'hue-rotate(30deg) saturate(0.8)',
                        animation: 'float 6s ease-in-out infinite',
                        '@keyframes float': {
                            '0%, 100%': { transform: 'translateY(0px)' },
                            '50%': { transform: 'translateY(-20px)' }
                        }
                    }}
                />
            </motion.div>

            <Container
                maxWidth="lg"
                sx={{
                    position: 'relative',
                    zIndex: 2,
                    mt: { xs: 0, md: -10 }
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        maxWidth: '700px',
                        mx: 'auto',
                        textAlign: 'center'
                    }}
                >
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <Typography
                            variant="h2"
                            sx={{
                                color: 'common.white',
                                fontWeight: 700,
                                mb: { xs: 3, md: 4 },
                                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' },
                                lineHeight: 1.3,
                                textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }}
                        >
                            Empowering businesses worldwide to{' '}
                            <Typography
                                component="span"
                                variant="inherit"
                                sx={{
                                    background: 'linear-gradient(90deg, #FF6B00 0%, #FF9D00 100%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    fontWeight: 700
                                }}
                            >
                                embrace payment
                            </Typography>
                        </Typography>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.3 }}
                    >
                        <Card
                            sx={{
                                backgroundColor: 'rgba(30, 37, 50, 0.8)',
                                backdropFilter: 'blur(8px)',
                                borderRadius: 3,
                                py: { xs: 2, md: 2.5 },
                                px: { xs: 1.5, md: 2 },
                                mb: { xs: 3, md: 4 },
                                width: { xs: '100%', md: '100%' },
                                border: '1px solid rgba(255, 107, 0, 0.2)',
                                transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                                '&:hover': {
                                    transform: 'translateY(-5px)',
                                    boxShadow: '0 8px 24px rgba(255, 107, 0, 0.2)'
                                }
                            }}
                        >
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={0}
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{
                                    '& > *:not(:last-child)': {
                                        borderBottom: { xs: '1px solid rgba(255, 107, 0, 0.1)', sm: 'none' },
                                        borderRight: { xs: 'none', sm: '1px solid rgba(255, 107, 0, 0.1)' },
                                        pb: { xs: 2, sm: 0 },
                                        mb: { xs: 2, sm: 0 },
                                        pr: { xs: 2, sm: 1 }
                                    }
                                }}
                            >
                                <StatItem
                                    label="Countries"
                                    value="30+"
                                    icon={<PublicIcon sx={{ fontSize: 22 }} />}
                                    index={0}
                                />
                                <StatItem
                                    label="Payment Methods"
                                    value="10+"
                                    icon={<PaymentIcon sx={{ fontSize: 22 }} />}
                                    index={1}
                                />
                                <StatItem
                                    label="Tokens"
                                    value="400+"
                                    icon={<TokenIcon sx={{ fontSize: 22 }} />}
                                    index={2}
                                />
                                <StatItem
                                    label="Blockchains"
                                    value="20+"
                                    icon={<AccountBalanceIcon sx={{ fontSize: 22 }} />}
                                    index={3}
                                />
                            </Stack>
                        </Card>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.6 }}
                        whileHover={{ scale: 1.05 }}
                    >
                        <Button
                            component="a"
                            href="https://cal.com/support-team-qsszsx"
                            target="_blank"
                            variant="contained"
                            size="large"
                            sx={{
                                py: 1.25,
                                px: 4,
                                borderRadius: 2,
                                fontSize: '0.9rem',
                                fontWeight: 600,
                                background: 'linear-gradient(90deg, #FF6B00 0%, #FF9D00 100%)',
                                '&:hover': {
                                    background: 'linear-gradient(90deg, #FF9D00 0%, #FF6B00 100%)',
                                },
                                boxShadow: '0 4px 14px 0 rgba(255, 107, 0, 0.4)',
                                position: 'relative',
                                overflow: 'hidden',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: '-100%',
                                    width: '100%',
                                    height: '100%',
                                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                                    animation: 'shimmer 2s infinite',
                                },
                                '@keyframes shimmer': {
                                    '100%': {
                                        left: '100%',
                                    },
                                }
                            }}
                        >
                            Talk to us
                        </Button>
                    </motion.div>
                </Box>
            </Container>

            {/* Dot pattern overlay with animation */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: '100%',
                    height: '100%',
                    backgroundImage: 'radial-gradient(circle, rgba(255, 107, 0, 0.03) 1px, transparent 1px)',
                    backgroundSize: '20px 20px',
                    opacity: 0.2,
                    zIndex: 0,
                    animation: 'fadeInOut 8s ease-in-out infinite',
                    '@keyframes fadeInOut': {
                        '0%, 100%': { opacity: 0.2 },
                        '50%': { opacity: 0.1 }
                    }
                }}
            />
        </Box>
    );
}; 